import { BadgeColors } from '@triumph/design-system';
import { orderBy, uniqBy } from 'es-toolkit';

import {
    ApplicantActivityResponse,
    AutomaticDeltaFundingMethodLinkState,
    BusinessInfoProps,
    DecisionDetailLink,
    DecisionDetailStatuses,
    TaxClassification,
    TaxClassificationLabels,
} from './Applicant.types';

// Count activity items not authored by the current reviewer.
// Notes and product status changes carry an email field; page visits don't
// (they're applicant-side events) so they always count.
export const getExternalActivityCount = (
    activity: ApplicantActivityResponse | null | undefined,
    currentUserEmail: string | undefined,
): number => {
    if (!activity) return 0;
    const externalNotes = activity.notes.filter(
        (n) => n.email?.toLowerCase() !== currentUserEmail?.toLowerCase(),
    ).length;
    const externalStatuses = activity.productStatuses.filter(
        (s) => s.email?.toLowerCase() !== currentUserEmail?.toLowerCase(),
    ).length;
    return externalNotes + externalStatuses + activity.pageVisits.length;
};

const statusColorMap: Record<DecisionDetailStatuses, BadgeColors> = {
    [DecisionDetailStatuses.CREATED]: BadgeColors.SECONDARY,
    [DecisionDetailStatuses.INVITED]: BadgeColors.SECONDARY,
    [DecisionDetailStatuses.LANDED]: BadgeColors.SECONDARY,
    [DecisionDetailStatuses.STARTED]: BadgeColors.SECONDARY,
    [DecisionDetailStatuses.UNDERREVIEW_MANUAL]: BadgeColors.SECONDARY,
    [DecisionDetailStatuses.UNDERREVIEW_AUTO]: BadgeColors.SECONDARY,
    [DecisionDetailStatuses.CONTRACTSENT]: BadgeColors.SECONDARY,

    [DecisionDetailStatuses.SUBMITTED]: BadgeColors.SUCCESS,
    [DecisionDetailStatuses.APPROVED]: BadgeColors.SUCCESS,
    [DecisionDetailStatuses.FINISHED]: BadgeColors.SUCCESS,

    [DecisionDetailStatuses.ERRORED]: BadgeColors.ERROR,
    [DecisionDetailStatuses.NEEDMOREINFO]: BadgeColors.ERROR,
    [DecisionDetailStatuses.DENIED]: BadgeColors.ERROR,
    [DecisionDetailStatuses.ABANDONED]: BadgeColors.ERROR,
};

export const getStatusVariant = (status: DecisionDetailStatuses) => statusColorMap[status];

// These mappings are defined here (from LoadPay)
// https://www.notion.so/triumphpay/LoadPay-Originations-Integration-Endpoints-3106b1d1b400805b9cf2cf0188a0f3bf
export const companyAccountStatusMap: Record<string, string> = {
    C: 'Canceled',
    D: 'Disabled',
    F: 'Failed Id Verification',
    J: 'Suspended Never Activated',
    K: 'Suspended',
    M: 'Moved To New Program',
    N: 'Active',
    P: 'Passed Id Verification',
    Q: 'Delinquent',
    R: 'Charged Of',
    T: 'Id Verification In Process',
    U: 'Upgraded',
    V: 'Application Submitted',
    W: 'Waiting To Be Processed',
    Z: 'Canceled Without Refund',
    d: 'Bankruptcy Discharged',
    e: 'Bankruptcy Dismissed',
    f: 'Bankruptcy Filed',
};

const deltaFundingMethodLinkStateMap: Record<AutomaticDeltaFundingMethodLinkState, (deltaClientId?: number | null) => string> = {
    [AutomaticDeltaFundingMethodLinkState.Linked]: (deltaClientId) =>
        `✅ LoadPay Bank Account automatically added as Funding Method for Delta ID: ${deltaClientId}`,
    [AutomaticDeltaFundingMethodLinkState.InProgressBankInfoReadyAndDeltaIdReady]: (deltaClientId) =>
        `⏳ Automatic adding of LoadPay Bank Account as Delta Funding Method... Delta ID ${deltaClientId}, LoadPay Bank Info Ready`,
    [AutomaticDeltaFundingMethodLinkState.InProgressBankInfoReadyAndDeltaIdPending]: () =>
        '⏳ Automatic adding of LoadPay Bank Account as Delta Funding Method... Delta ID Pending, LoadPay Bank Info Ready',
    [AutomaticDeltaFundingMethodLinkState.InProgressBankInfoPendingAndDeltaIdReady]: (deltaClientId) =>
        `⏳ Automatic adding of LoadPay Bank Account as Delta Funding Method... Delta ID ${deltaClientId}, LoadPay Bank Info Pending`,
    [AutomaticDeltaFundingMethodLinkState.InProgressBankInfoPendingAndDeltaIdPending]: () =>
        '⏳ Automatic adding of LoadPay Bank Account as Delta Funding Method... Delta ID Pending, LoadPay Bank Info Pending',
    [AutomaticDeltaFundingMethodLinkState.SuspectedRelationshipDisabled]: () =>
        '⚠️ Automatic adding of LoadPay Bank Account as Delta Funding Method is disabled because there exists a Suspected Relationship',
};

// The live applicant sends the raw enum number; payloads serialized through the backend BusinessInfo
// DTO send the member name, so resolve both. Unknown reads as no value, not as "Unknown".
export const getTaxClassificationLabel = (
    taxClassification: BusinessInfoProps['taxClassification'],
): string | undefined => {
    if (taxClassification == null) return undefined;

    const value =
        typeof taxClassification === 'number'
            ? taxClassification
            : TaxClassification[taxClassification];

    return TaxClassificationLabels[value] || undefined;
};

export const getDeltaFundingMethodLinkStatusDisplay = (
    state: AutomaticDeltaFundingMethodLinkState,
    deltaClientId?: number | null,
): string => deltaFundingMethodLinkStateMap[state](deltaClientId);

// Numbers a product's Submitted statuses from the oldest, keyed by status id, so the newest carries the status bar's
// count. Links are join rows, so one status can appear more than once; it is numbered once. Ties go to the lower id,
// as the Activity tab's server-side numbering breaks them.
export const getSubmittedOrdinals = (links: DecisionDetailLink[] | undefined): Map<string, number> => {
    const submitted = uniqBy(
        (links ?? [])
            .map(({ decisionDetail }) => decisionDetail)
            .filter(({ status }) => status === DecisionDetailStatuses.SUBMITTED),
        ({ id }) => id,
    );

    return new Map(orderBy(submitted, ['dateCreated', 'id'], ['asc', 'asc']).map(({ id }, index) => [id, index + 1]));
};
